import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { AppShell } from '../../app/AppShell'
import { api } from '../../api/client'
import { useQueryClient } from '@tanstack/react-query'
import { characterKeys, useCharacterSheet } from '../../domains/characters/queries'
import { galleryKeys, useGallery } from '../../domains/images/queries'
import { ImageGallery } from '../../domains/images/ImageGallery'
import { GenerationCard } from '../../domains/jobs/GenerationCard'
import { useGenerationJob } from '../../domains/jobs/useGenerationJob'
import type { GenerationJob } from '../../domains/jobs/types'
import { Button, Card, Input, TabPanel, Tabs } from '../../design/components'
import styles from '../play/play.module.css'

/** Which half of the screen is showing. */
type ImagesTab = 'gallery' | 'generate'

/**
 * A character's pictures: the ones that exist, and the three ways to make more.
 *
 * Kept off the sheet rather than folded into it: a sheet is already fourteen sections long
 * on a phone, and a gallery is a screen's worth of thumbnails. The sheet links here.
 *
 * **Two tabs rather than one scroll.** Looking at a character's pictures and making one are
 * different errands, and stacking them meant scrolling past three generation cards to reach
 * the gallery — or past the whole gallery to reach the generators. They are alternatives, which
 * is exactly what tabs are for; the sheet's own sections are not, which is why it has none.
 */
export function CharacterImagesPage() {
  const params = useParams<{ id: string }>()
  const characterId = Number(params.id)

  const queryClient = useQueryClient()
  const sheet = useCharacterSheet(characterId, null)
  const [artDirection, setArtDirection] = useState('')
  const [tab, setTab] = useState<ImagesTab>('gallery')

  const owner = { kind: 'character' as const, characterId }
  const gallery = useGallery(owner)

  /**
   * Both refetches are the point of the job, not an afterthought: a finished image job
   * returns a *pointer* to something already saved and linked, so the gallery and the
   * sheet (whose header shows the portrait) are what actually tell the user it worked.
   */
  const refetchOwner = () => {
    void queryClient.invalidateQueries({ queryKey: galleryKeys.gallery(owner) })
    void queryClient.invalidateQueries({ queryKey: characterKeys.sheet(characterId, null) })
  }

  const portrait = useGenerationJob({
    start: () =>
      api.post<GenerationJob>(`/characters/${characterId}/portrait/generate`, {
        additionalDescription: artDirection === '' ? null : artDirection,
        referenceCharacterId: null,
      }),
    onSettled: refetchOwner,
  })

  const description = useGenerationJob({
    start: () => api.post<GenerationJob>(`/characters/${characterId}/description/generate`),
    // Nothing to refetch: this one answers with prose for the owner to read and save
    // deliberately, and the API never writes it to the bio itself.
  })

  const isOwner = sheet.data?.isOwner === true
  const name = sheet.data?.sections.core?.name

  // On the tab rather than only inside the panel, so the count is readable from the other one.
  const count = gallery.data?.images?.length
  const galleryCount = count === undefined ? undefined : String(count)

  if (Number.isNaN(characterId)) {
    return (
      <AppShell title="Images">
        <Card>
          <p className={styles.note}>That character id is not a number.</p>
        </Card>
      </AppShell>
    )
  }

  return (
    <AppShell title={name ?? 'Images'} subtitle="IMAGES">
      <Tabs
        label="Images"
        active={tab}
        onSelect={setTab}
        tabs={[
          { id: 'gallery', label: 'Gallery', hint: galleryCount },
          { id: 'generate', label: 'Generate' },
        ]}
      />

      {tab === 'gallery' && (
        <TabPanel id="gallery">
          <ImageGallery owner={owner} label="Gallery" />
        </TabPanel>
      )}

      {tab === 'generate' && (
        <TabPanel id="generate">
          {!isOwner && (
            <Card>
              <p className={styles.note}>
                Only this character&apos;s owner can generate pictures for them.
              </p>
            </Card>
          )}

          {/* Three ways to make something, in the order you would reach for them. The first
              is a workbench, the second a button, and the third runs the other direction —
              from a picture to words. */}
          {isOwner && (
            <Card variant="magic" label="Compose a portrait">
              <p className={styles.note}>
                Build the prompt yourself out of text and reference pictures, turn the model&apos;s
                knobs, and keep only the results worth keeping.
              </p>
              <Link to={`/characters/${characterId}/portrait`}>
                <Button fullWidth>Open the composer</Button>
              </Link>
            </Card>
          )}

          <GenerationCard
            label="Generate a portrait"
            what="The quick version: the generator reads this character's bio and aspects and saves the result straight to the gallery. It runs in the background; the gallery refreshes when it lands."
            action="Generate a portrait"
            canGenerate={isOwner}
            job={portrait}
          >
            <Input
              label="Art direction"
              placeholder="Rain-soaked, neon behind him"
              hint="Optional — layered on top of the bio the generator already reads."
              value={artDirection}
              onChange={(event) => setArtDirection(event.target.value)}
            />
          </GenerationCard>

          <GenerationCard
            label="Describe the portrait"
            what="Reads the character's existing portrait and writes a description of it. The text comes back for you to review — it is never written to the bio for you."
            action="Write a description"
            canGenerate={isOwner}
            job={description}
            renderText={(text) => <p className={styles.rowText}>{text}</p>}
          />
        </TabPanel>
      )}

      <Link to={`/characters/${characterId}`} className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          Back to the sheet
        </Button>
      </Link>
    </AppShell>
  )
}
